import { zodResolver } from "@hookform/resolvers/zod";
import { useSetActingPersonMutation } from "Api/Mutations/Party/useSetActingPersonMutation";
import { useUpdateVerificationsMutation } from "Api/Mutations/Verifications/useUpdateVerificationsMutation";
import { HunterIncomeInformationFields } from "Components/Onboarding/BusinessEntity/Steps/IncomeInformationStep/HunterIncomeInformationFields";
import { MerchantIncomeInformationFields } from "Components/Onboarding/BusinessEntity/Steps/IncomeInformationStep/MerchantIncomeInformationFields";
import {
  getSchema,
  type IncomeInformationStepFormData,
} from "Components/Onboarding/BusinessEntity/Steps/IncomeInformationStep/schema";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { FullHeightForm } from "Components/Shared/FormStyles";
import { useIsCurrentUserHunter } from "Hooks/useIsCurrentUserHunter";
import {
  CountryCode,
  PartyType,
  type RelatedPartyDto,
  type SourceOfIncome,
  type UserVerificationDto,
} from "Infrastructure/Api/Api";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  onNextStepRequested: () => void;
  verificationData: UserVerificationDto;
  partyType: PartyType;
  companyPartyPublicID: string;
  actingPersonPartyRelation?: RelatedPartyDto;
};

export const IncomeInformationStepContent = ({
  onNextStepRequested,
  verificationData,
  partyType,
  companyPartyPublicID,
  actingPersonPartyRelation,
}: Props) => {
  const { t } = useResource();
  const isUserHunter = useIsCurrentUserHunter();

  // The acting person is the signed-in person themselves, so the server links their own Party —
  // creating one here would leave the company pointing at a blank duplicate of them.
  const { mutate: setActingPerson, isPending: isActingPersonUpdating } =
    useSetActingPersonMutation({
      onSuccess: () => onNextStepRequested(),
      onError: () =>
        toast.error(
          t(
            Resources.Onboarding.BusinessOnboarding.Steps.IncomeInformation
              .SaveError,
          ),
        ),
    });

  const { mutateAsync: putVerification, isPending: isVerificationMutating } =
    useUpdateVerificationsMutation({});

  const validationSchema = getSchema(t, isUserHunter, partyType);
  const defaultValues: Partial<IncomeInformationStepFormData> = {
    ...verificationData,
    sourcesOfIncome: verificationData.sourcesOfIncome ?? [],
    businessActivity: verificationData.businessActivity ?? undefined,
    taxResidency: verificationData.taxResidency ?? CountryCode.CZ, //To preselect Czechia (the home market)
    relationType: actingPersonPartyRelation?.relationType,
  };

  const { control, handleSubmit, watch } =
    useForm<IncomeInformationStepFormData>({
      resolver: zodResolver(validationSchema),
      defaultValues,
    });

  const isPoliticallyExposedPerson = watch("isPoliticallyExposedPerson");
  const businessActivity = watch("businessActivity") ?? undefined;
  const sourcesOfIncome: Array<SourceOfIncome> = watch("sourcesOfIncome") ?? [];

  const onSubmit = async (data: IncomeInformationStepFormData) => {
    try {
      await putVerification({
        ...data,
        uboUnknown: verificationData.uboUnknown ?? false,
      });

      if (partyType !== PartyType.LegalEntity) {
        onNextStepRequested();
        return;
      }

      if (data.relationType) {
        setActingPerson({
          companyPartyPublicID,
          relationType: data.relationType,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(
        t(
          Resources.Onboarding.BusinessOnboarding.Steps.IncomeInformation
            .SaveError,
        ),
      );
    }
  };

  return (
    <FullHeightForm onSubmit={handleSubmit(onSubmit)}>
      <StepBox
        submitButtonLabel={t(Resources.Common.Continue)}
        submitDisabled={isPoliticallyExposedPerson}
        isSubmitting={isVerificationMutating || isActingPersonUpdating}
      >
        {isUserHunter ? (
          <HunterIncomeInformationFields
            control={control}
            partyType={partyType}
            sourcesOfIncome={sourcesOfIncome}
            businessActivity={businessActivity}
            isPoliticallyExposedPerson={isPoliticallyExposedPerson}
          />
        ) : (
          <MerchantIncomeInformationFields
            control={control}
            partyType={partyType}
            sourcesOfIncome={sourcesOfIncome}
            businessActivity={businessActivity}
            isPoliticallyExposedPerson={isPoliticallyExposedPerson}
          />
        )}
      </StepBox>
    </FullHeightForm>
  );
};
